/* US rewards display only: EG and Shopify remain responsible for fulfillment. */
(() => {
  const supportedMarket = (config, cartCurrency = config.currency) =>
    config.country === 'US'
    && config.currency === 'USD'
    && cartCurrency === 'USD'
    && Number.isSafeInteger(config.giftGoal)
    && config.giftGoal > 0
    && Number.isSafeInteger(config.shippingGoal)
    && config.shippingGoal >= config.giftGoal;

  const rewardState = (cart, config) => {
    // A temporarily paid EG gift must never help qualify itself for a reward.
    const giftTotal = cart.items.reduce((total, item) =>
      total + (item.properties?._Gifted ? Math.max(0, item.final_line_price) : 0), 0);
    const subtotal = Math.max(0, cart.total_price - giftTotal);
    const originalTotal = cart.original_total_price ?? cart.items.reduce((total, item) =>
      total + (item.original_line_price ?? item.final_line_price), 0);
    const originalGiftTotal = cart.items.reduce((total, item) => total + (item.properties?._Gifted
      ? Math.max(0, item.original_line_price ?? item.final_line_price) : 0), 0);
    const giftSubtotal = config.giftUsesDiscountedPrice
      ? subtotal : Math.max(0, originalTotal - originalGiftTotal);
    const hasItems = cart.items.some((item) => !item.properties?._Gifted && item.quantity > 0);
    const giftUnlocked = giftSubtotal >= config.giftGoal;
    const shippingUnlocked = subtotal >= config.shippingGoal;
    const goal = giftUnlocked ? config.shippingGoal : config.giftGoal;
    const progressTotal = giftUnlocked ? subtotal : giftSubtotal;
    return {
      subtotal,
      giftSubtotal,
      progressTotal,
      hidden: !hasItems || !supportedMarket(config, cart.currency),
      giftUnlocked,
      shippingUnlocked,
      goal,
      remaining: Math.max(0, goal - progressTotal),
      percent: Math.min(100, progressTotal / goal * 100),
    };
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { supportedMarket, rewardState };
  }
  if (typeof document === 'undefined') return;

  const configElement = document.querySelector('[data-rm-native-rewards-config]');
  if (!configElement) return;
  const config = JSON.parse(configElement.textContent);
  const { copy } = config;
  const formatMoney = (cents) => new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', maximumFractionDigits: 2,
    minimumFractionDigits: cents % 100 ? 2 : 0,
  }).format(cents / 100);
  let timer;
  let revision = 0;

  const syncShippingTrust = () => {
    document.querySelectorAll('[data-rm-shipping-trust]').forEach((feature) => {
      feature.hidden = !supportedMarket(config);
      if (feature.hidden) return;
      const value = feature.querySelector('[data-rm-shipping-trust-value]');
      const label = feature.querySelector('[data-rm-shipping-trust-label]');
      if (value) value.textContent = `${copy.shippingTrust} ${formatMoney(config.shippingGoal)}`;
      if (label) label.textContent = copy.countryName;
    });
  };

  const makeRoadmap = () => {
    const roadmap = document.createElement('section');
    roadmap.className = 'rm-cart-rewards';
    roadmap.dataset.rmCartRewards = '';
    roadmap.dataset.nativeRewardRoadmap = '';
    roadmap.hidden = true;
    roadmap.setAttribute('aria-label', copy.title);
    const title = document.createElement('p');
    title.className = 'rm-cart-rewards__title';
    title.textContent = copy.title;
    const list = document.createElement('ol');
    list.className = 'rm-cart-rewards__list';
    [['gift', copy.gift, config.giftGoal], ['shipping', copy.shipping, config.shippingGoal]]
      .forEach(([key, label, threshold], index) => {
        const item = document.createElement('li');
        item.className = 'rm-cart-rewards__goal';
        item.dataset.rewardKey = key;
        item.innerHTML = '<span class="rm-cart-rewards__marker" data-reward-marker aria-hidden="true"></span>'
          + '<span class="rm-cart-rewards__copy"><span class="rm-cart-rewards__name"></span>'
          + '<strong class="rm-cart-rewards__amount"></strong><span class="sr-only" data-reward-state-label></span></span>';
        item.querySelector('[data-reward-marker]').textContent = String(index + 1);
        item.querySelector('.rm-cart-rewards__name').textContent = label;
        item.querySelector('.rm-cart-rewards__amount').textContent = `${copy.from} ${formatMoney(threshold)}`;
        if (key === 'gift' && !config.giftUsesDiscountedPrice) {
          const basis = document.createElement('span');
          basis.className = 'rm-cart-rewards__basis';
          basis.textContent = copy.giftBasis || 'before discounts';
          item.querySelector('.rm-cart-rewards__copy').append(basis);
        }
        list.append(item);
      });
    roadmap.append(title, list);
    return roadmap;
  };

  const makeBar = () => {
    const bar = document.createElement('div');
    bar.className = 'rm-cart-rewards-bar';
    bar.dataset.nativeRewardBar = '';
    bar.hidden = true;
    bar.innerHTML = '<p class="rm-cart-rewards-bar__message" role="status" aria-live="polite" aria-atomic="true"></p>'
      + '<div class="rm-cart-rewards-bar__track" role="progressbar" aria-valuemin="0">'
      + '<div class="rm-cart-rewards-bar__fill"></div></div>';
    return bar;
  };

  const cartSurfaces = () => {
    const surfaces = [];
    document.querySelectorAll('main [data-cart-items], [data-cart-drawer] [data-cart-items]')
      .forEach((items) => {
        const drawer = items.closest('[data-cart-drawer]');
        const context = drawer ? 'drawer' : 'page';
        const footer = drawer?.querySelector('.rm-cart-drawer__footer')
          || items.closest('main')?.querySelector('[data-cart-footer] .rm-cart-footer__summary');
        if (!footer) return;
        const parent = items.parentElement;
        let roadmap = parent.querySelector(':scope > [data-native-reward-roadmap]');
        if (!roadmap) {
          roadmap = makeRoadmap();
          items.before(roadmap);
        }
        parent.dataset.rmCartRewardsRoadmapContext = context;
        let host = footer.querySelector(':scope > [data-rm-native-rewards-wrapper]');
        if (!host) {
          host = document.createElement('div');
          host.hidden = true;
          host.dataset.rmNativeRewardsWrapper = '';
          host.dataset.rmCartRewardsBarWrapper = '';
          host.dataset.rmCartRewardsBarContext = context;
          host.append(makeBar());
          footer.prepend(host);
        }
        surfaces.push({ roadmap, host, bar: host.firstElementChild });
      });
    return surfaces;
  };

  const hideRewards = () => {
    document.querySelectorAll('[data-native-reward-roadmap], [data-native-reward-bar], [data-rm-native-rewards-wrapper]')
      .forEach((element) => { element.hidden = true; });
  };

  const updateSurface = ({ roadmap, host, bar }, state) => {
    roadmap.hidden = state.hidden;
    host.hidden = state.hidden;
    bar.hidden = state.hidden;
    if (state.hidden) return;
    ['gift', 'shipping'].forEach((key, index) => {
      const element = roadmap.querySelector(`[data-reward-key="${key}"]`);
      const completed = key === 'gift' ? state.giftUnlocked : state.shippingUnlocked;
      const current = !completed && (key === 'gift' || state.giftUnlocked);
      element.dataset.rewardState = completed ? 'completed' : current ? 'current' : 'upcoming';
      element.querySelector('[data-reward-marker]').textContent = completed ? '\u2713' : String(index + 1);
      element.querySelector('[data-reward-state-label]').textContent =
        completed ? copy.unlocked : current ? copy.current : copy.upcoming;
      if (current) element.setAttribute('aria-current', 'step');
      else element.removeAttribute('aria-current');
    });
    const message = bar.querySelector('.rm-cart-rewards-bar__message');
    const phrase = state.shippingUnlocked ? copy.complete
      : state.giftUnlocked ? copy.shippingRemaining : copy.giftRemaining;
    const nextMessage = phrase.replace('{amount}', formatMoney(state.remaining));
    if (message.textContent !== nextMessage) {
      const [before, after] = phrase.split('{amount}');
      if (after === undefined) message.textContent = phrase;
      else {
        const amount = document.createElement('strong');
        amount.textContent = formatMoney(state.remaining);
        message.replaceChildren(document.createTextNode(before), amount, document.createTextNode(after));
      }
    }
    const track = bar.querySelector('[role="progressbar"]');
    track.setAttribute('aria-label', state.giftUnlocked ? copy.shipping : copy.gift);
    track.setAttribute('aria-valuemax', String(state.goal / 100));
    track.setAttribute('aria-valuenow', String(Math.min(state.progressTotal, state.goal) / 100));
    track.setAttribute('aria-valuetext', nextMessage);
    bar.querySelector('.rm-cart-rewards-bar__fill').style.width = `${state.percent}%`;
  };

  const refresh = async (expectedRevision) => {
    syncShippingTrust();
    if (!supportedMarket(config)) { hideRewards(); return; }
    if (!cartSurfaces().length) return;
    try {
      const separator = config.cartUrl.includes('?') ? '&' : '?';
      const response = await fetch(`${config.cartUrl}${separator}_rm_rewards=${Date.now()}`, {
        credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' },
      });
      if (expectedRevision !== revision) return;
      if (!response.ok) { hideRewards(); return; }
      const cart = await response.json();
      if (expectedRevision !== revision) return;
      const state = rewardState(cart, config);
      cartSurfaces().forEach((surface) => updateSurface(surface, state));
    } catch (error) {
      // Hide stale promises without affecting cart edits or checkout.
      if (expectedRevision === revision) hideRewards();
    }
  };

  const schedule = () => {
    const expectedRevision = ++revision;
    window.clearTimeout(timer);
    timer = window.setTimeout(() => refresh(expectedRevision), 180);
  };

  const start = () => {
    syncShippingTrust();
    schedule();
    const surfaceSelector = '[data-cart-items], [data-cart-footer], [data-cart-drawer], '
      + '.rm-cart-footer__summary, .rm-cart-drawer__footer';
    const observer = new MutationObserver((mutations) => {
      const hasCartChange = mutations.some((mutation) =>
        [...mutation.addedNodes, ...mutation.removedNodes].some((node) =>
          node instanceof Element && (node.matches(surfaceSelector) || node.querySelector(surfaceSelector))));
      if (hasCartChange) schedule();
    });
    observer.observe(document.body, { childList: true, subtree: true });
  };

  ['shapes:cart:update', 'shapes:cart:unlock', 'shapes:cart:afteradditem',
    'shapes:cart:cartqtychange', 'theme:cart-drawer:open', 'shopify:section:load']
    .forEach((event) => document.addEventListener(event, schedule));
  window.addEventListener('pageshow', schedule);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) schedule(); });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
